import { useEffect, useState } from 'react';
import { VOICE_INTAKE_PROMPT } from '../lib/voiceIntakePrompt';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Modal that surfaces the voice intake prompt with a copy button. The operator
 * pastes the prompt into Claude / ChatGPT / Gemini voice mode, runs the
 * interview, then pastes the structured output back into the brief textarea.
 */
export function VoiceIntakeModal({ open, onClose }: Props) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  if (!open) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(VOICE_INTAKE_PROMPT);
      setCopied(true);
    } catch {
      // Fallback: select the textarea so the operator can Cmd+C manually.
      const ta = document.getElementById('voice-intake-prompt-textarea') as HTMLTextAreaElement | null;
      if (ta) { ta.focus(); ta.select(); }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-background-dark/60 backdrop-blur-[2px] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="voice-intake-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] flex flex-col bg-surface border border-border-color rounded-xl shadow-2xl shadow-black/60 overflow-hidden"
      >
        <header className="flex items-start justify-between gap-4 px-6 py-5 border-b border-border-color">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent-cyan">Step 1</p>
            <h2 id="voice-intake-title" className="font-display text-2xl font-bold tracking-tight mt-1 text-text-primary">
              Voice intake prompt
            </h2>
            <p className="text-sm text-muted mt-2 leading-relaxed max-w-xl">
              Copy this prompt. Either run it yourself in Claude, ChatGPT, or Gemini voice mode, or send it to the brand's founder. The interview takes 30 to 45 minutes spoken; the LLM tells the runner to switch to voice mode before starting. At the end you'll get a 16-section markdown brief to paste into the brief field. The prompt is self-contained — no mention of Bubbles, safe to share.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="material-symbols-outlined text-muted hover:text-text-primary transition-colors -mt-1"
          >
            close
          </button>
        </header>

        <div className="flex-1 overflow-auto px-6 py-4">
          <textarea
            id="voice-intake-prompt-textarea"
            readOnly
            value={VOICE_INTAKE_PROMPT}
            onClick={(e) => (e.target as HTMLTextAreaElement).select()}
            className="w-full h-[50vh] bg-background-dark border border-border-color rounded px-3 py-2 text-xs font-mono leading-relaxed text-text-primary resize-none focus:outline-none focus:border-accent-cyan transition-colors"
          />
        </div>

        <footer className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border-color bg-surface-2/40">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
            {VOICE_INTAKE_PROMPT.length.toLocaleString()} chars · Esc to close
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="font-mono text-[11px] uppercase tracking-wider px-3 py-2 text-muted hover:text-text-primary transition-colors"
            >
              Done
            </button>
            <button
              onClick={copy}
              className={`font-mono text-[11px] uppercase tracking-wider px-4 py-2 rounded transition-colors flex items-center gap-2 ${
                copied
                  ? 'bg-accent-emerald/20 text-accent-emerald border border-accent-emerald/40'
                  : 'bg-accent-cyan/15 text-accent-cyan border border-accent-cyan/40 hover:bg-accent-cyan/25'
              }`}
            >
              <span className="material-symbols-outlined text-[16px]">{copied ? 'check' : 'content_copy'}</span>
              <span>{copied ? 'Copied' : 'Copy prompt'}</span>
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
